import { makeAutoObservable, runInAction } from "mobx";
import twColors from "tailwindcss/colors";

import { twColorToHex } from "@/utils/tailwindColors";

import { GRAPH_SCALE_CONTAINER_CLASS } from "./constants";
import { calculateGraphCoordsSystem } from "./utils";

import type { GGBApi, GraphOptions } from "@/components/graph/types";

type GGBApplet = {
  inject: (id: string | HTMLElement) => void;
};

const APPLET_PARAMS = {
  showMenuBar: false,
  showAlgebraInput: false,
  showToolBar: false,
  showToolBarHelp: false,
  showResetIcon: false,
  enableLabelDrags: false,
  enableShiftDragZoom: true,
  enableRightClick: false,
  errorDialogsActive: false,
  useBrowserForJS: true,
  preventFocus: true,
  showKeyboardOnFocus: false,
  language: "en",
  borderColor: "transparent",
  scaleContainerClass: GRAPH_SCALE_CONTAINER_CLASS,
};

const APPLET_VIEWS = {
  is3D: 0,
  AV: 0,
  SV: 0,
  CV: 0,
  EV2: 1,
  CP: 0,
  PC: 0,
  DA: 0,
  FI: 0,
  PV: 0,
  macro: 1,
};

declare global {
  interface Window {
    GGBApplet?: new (
      ggbVersion?: string,
      parameters?: object,
      views?: object,
      html5NoWebSimple?: boolean,
    ) => GGBApplet;
  }
}

class GraphManager {
  ggbReady = typeof window === "undefined" ? false : !!window.GGBApplet;

  constructor() {
    makeAutoObservable(this);
  }

  init() {
    if (this.ggbReady) return;

    let el: HTMLScriptElement | null = document.head.querySelector("#ggb-lib");

    if (!el) {
      el = document.createElement("script");
      el.id = "ggb-lib";
      el.src = "https://www.geogebra.org/apps/deployggb.js";
      document.head.appendChild(el);
    }

    el.addEventListener("load", () => {
      if (window.GGBApplet) {
        runInAction(() => {
          this.ggbReady = true;
        });
      }
    });
  }

  dispose(): void {}

  async createApplet(opts: {
    abort: AbortSignal;
    container: HTMLElement;
    options?: Partial<GraphOptions>;
  }) {
    return new Promise<GGBApi | null>((res) => {
      const { abort, container, options = {} } = opts;
      let { width, height } = options;

      if (!window.GGBApplet) {
        res(null);
        return;
      }

      if (abort.aborted) {
        res(null);
        return;
      }

      if (!width || !height) {
        const rect = container.getBoundingClientRect();
        width ??= rect.width;
        height ??= rect.height;
      }

      const params = {
        ...APPLET_PARAMS,
        width,
        height,
        appletOnLoad: (obj: GGBApi) => {
          if (abort.aborted) {
            res(null);
            return;
          }

          // GeoGebra injects an aria-label on a role-less div, which ARIA prohibits
          container
            .querySelector("[id^='ggbApplet'][aria-label]")
            ?.setAttribute("role", "application");

          obj.setPerspective("G");
          obj.setGraphicsOptions(1, {
            gridType: options.gridType ?? 0,
            gridColor: twColorToHex(options.gridColor) ?? twColors.gray["200"],
            bgColor: twColorToHex(options.bgColor) ?? "#ffffff",
            axesColor: twColorToHex(options.axesColor) ?? "#000000",
            rulerType: 2,
          });
          obj.setAxesVisible(1, options.axesVisible ?? true, options.axesVisible ?? true);

          const coordsSystem = calculateGraphCoordsSystem({
            width,
            height,
            zoom: options.zoom,
            coordsSystem: options.coordsSystem,
          });

          if (coordsSystem) {
            obj.setCoordSystem(...coordsSystem);
          }

          res(obj);
        },
      };

      const applet = new window.GGBApplet("5.0", params, APPLET_VIEWS);

      applet.inject(container);
    });
  }
}

export const graphManager = new GraphManager();
